import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { Row, Col, Layout, Modal, Button, Tabs, Space } from "antd";
import CustomerDetail from "../CustomerDetail/CustomerDetail";
import { CreatePaymentDetail } from "../Payment/CreatePaymentDetail";
import Quotes from "../../widgets/Quotes/Quotes.widget";
import Policies from "../../widgets/Policies/Policies.widget";
import { setRecentlyViewedCustomer } from "../../state/pages/customer/actionCreators";
import { getFeatureFlagsConfiguration } from "../../utils/getGlobalConfiguration";
import { customerDetailsPath, customerDetailsPaymentPath } from "../../router/paths";
import Opportunities from "../../widgets/Opportunities/Opportunities.widget";
import { FeatureFlag } from "../../components/FeatureFlag/FeatureFlag";
import { featureFlags } from "../../components/FeatureFlag/featureFlagConst";
import { Documents } from "../../widgets/DocumentsWidget/Documents.widget";
import { RelationWidget } from "../../widgets/RelationWidget/RelationWidget";
import { SearchMode } from "@tia/agent-api-connector/dist/enums/SearchMode";
import Payments from "../Payments/Payment";
import { ProfileOutlined, EuroCircleOutlined } from "@ant-design/icons";
import "./Customer.style.scss";

const Customer = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { customerid } = useParams<{ customerid: string }>();
  const dispatch = useDispatch();

  const documentFeature = getFeatureFlagsConfiguration("customer.documentsWidget.customer.view");

  documentFeature.disableMergeCheckbox = true;
  documentFeature.disableMergeButton = true;

  const [showModal, setShowModal] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCustomWidget, setShowCustomWidget] = useState(false);
  const [currentTab, setCurrentTab] = useState("portfolio");

  useEffect(() => {
    dispatch(setRecentlyViewedCustomer(customerid));
  }, [customerid]);

  useEffect(() => {
    if (location.pathname === customerDetailsPaymentPath(customerid)) {
      setCurrentTab("payments");
    }
  }, []);

  useEffect(() => {
    if (
      getFeatureFlagsConfiguration(featureFlags.customer.customerView.paymentTab) &&
      location.pathname === customerDetailsPath(customerid)
    ) {
      setCurrentTab("portfolio");
    }
  }, [location.pathname]);

  const handleEditRelation = (id: number) => {
    history.push(customerDetailsPath(id.toString()));
  };

  const handleTabChange = (tab: string) => {
    setCurrentTab(tab);
    if (tab === "payments") {
      history.push(customerDetailsPaymentPath(customerid));
    } else {
      history.push(customerDetailsPath(customerid));
    }
  };

  if (redirect) {
    return <Redirect to="/customers" />;
  }

  const items = [
    {
      key: "portfolio",
      label: (
        <div data-cy="customer-tab-portfolio">
          <ProfileOutlined /> {t("pages.customer.tabs.portfolio")}
        </div>
      ),
    },
    {
      key: "payments",
      label: (
        <div data-cy="customer-tab-payments">
          <EuroCircleOutlined /> {t("pages.customer.tabs.payments")}
        </div>
      ),
    },
  ];

  return (
    <Layout.Content style={{ marginTop: "10px" }}>
      <Modal title={t("pages.customer.errorModal.title")} closable={false} open={showModal} footer={null}>
        {t("pages.customer.errorModal.text")}
        <div style={{ textAlign: "right" }}>
          <Button
            type="primary"
            onClick={() => {
              setRedirect(true);
              setShowModal(false);
            }}
          >
            {t("pages.customer.errorModal.buttonText")}
          </Button>
        </div>
      </Modal>
      <FeatureFlag featureFlag={featureFlags.customer.customerView.paymentTab}>
        <Tabs items={items} activeKey={currentTab} onChange={handleTabChange} type="card" />
      </FeatureFlag>
      <Row gutter={40}>
        <Col xs={24} lg={8}>
          <FeatureFlag featureFlag={featureFlags.customer.customerView.customerDetail}>
            <CustomerDetail addPaymentDetails={() => setShowPaymentModal(true)} />
          </FeatureFlag>
          <FeatureFlag featureFlag={featureFlags.customer.customerView.createPaymentDetail}>
            <CreatePaymentDetail
              partyId={customerid}
              showPaymentModal={showPaymentModal}
              showCustomWidget={showCustomWidget}
              handleShowCustomWidget={() => setShowCustomWidget(true)}
              closePaymentModal={() => {
                setShowPaymentModal(false);
                setShowCustomWidget(false);
              }}
            />
          </FeatureFlag>
          <FeatureFlag featureFlag={featureFlags.customer.documentsWidget.customer.view.widget}>
            <Documents
              config={getFeatureFlagsConfiguration("customer.documentsWidget.customer.view")}
              partyId={customerid}
              searchMode={SearchMode.Party}
              featureFlag={documentFeature}
            />
          </FeatureFlag>
          <FeatureFlag featureFlag={featureFlags.customer.customerView.viewRelations}>
            <RelationWidget
              onEditRelation={(customerId: number) => handleEditRelation(customerId)}
              ownerId={customerid}
            />
          </FeatureFlag>
        </Col>
        <Col xs={24} lg={16}>
          {currentTab === "portfolio" && (
            <Space orientation="vertical" size="middle" style={{ display: "flex" }}>
              <FeatureFlag featureFlag={featureFlags.customer.customerView.policies}>
                <Policies id={customerid} />
              </FeatureFlag>
              <FeatureFlag featureFlag={featureFlags.customer.customerView.quotes}>
                <Quotes id={customerid} />
              </FeatureFlag>
              <FeatureFlag featureFlag={featureFlags.customer.customerView.opportunities}>
                <Opportunities id={customerid} />
              </FeatureFlag>
            </Space>
          )}
          {currentTab === "payments" && (
            <>
              <FeatureFlag featureFlag={featureFlags.customer.customerView.paymentTab}>
                <Payments partyId={customerid} />
              </FeatureFlag>
            </>
          )}
        </Col>
      </Row>
    </Layout.Content>
  );
};
export default Customer;
